import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Calendar, MapPin, Users, Pencil, Trash2, ExternalLink, ArrowRight, Eye, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Badge, Modal, Input, Textarea, SelectField, PageHeader, EmptyState, ConfirmDialog, Spinner } from '../components/ui';
import { createRecommendedHomeLayout, type ConferenceEvent } from '@conference/shared';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminEventService, type AdminEvent } from '../services/adminEventService';
import { eventBuilderService } from '../services/eventBuilderService';
import { manualEventModuleDefaults } from '../utils/eventWorkspace';

// ─── Form schema ──────────────────────────────────────────────────────────────

const eventSchema = z.object({
  name:         z.string().min(3, 'Name must be at least 3 characters'),
  description:  z.string().optional(),
  date_start:   z.string().min(1, 'Start date is required'),
  date_end:     z.string().min(1, 'End date is required'),
  location:     z.string().min(2, 'Location is required'),
  venue:        z.string().optional(),
  timezone:     z.string().min(1, 'Timezone is required'),
  status:       z.enum(['draft', 'published', 'live', 'completed', 'cancelled']),
  public_preview: z.boolean().default(false),
  max_attendees: z.coerce.number().optional(),
  website:      z.string().optional(),
  image_url:    z.string().optional(),
});

type EventForm = z.input<typeof eventSchema>;

// ─── Status badge ─────────────────────────────────────────────────────────────

function statusBadge(status: ConferenceEvent['status']) {
  const map = {
    draft:     { variant: 'default', label: 'Draft' },
    published: { variant: 'info',    label: 'Published' },
    live:      { variant: 'success', label: 'Live' },
    completed: { variant: 'purple',  label: 'Completed' },
    cancelled: { variant: 'danger',  label: 'Cancelled' },
  } as const;
  const { variant, label } = map[status] ?? { variant: 'default', label: status };
  return <Badge variant={variant}>{label}</Badge>;
}

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Singapore', 'Australia/Sydney', 'UTC',
];

// ─── Event form modal ─────────────────────────────────────────────────────────

function EventModal({ open, onClose, event, onSaved, workspaceId }: {
  open: boolean;
  onClose: () => void;
  event: ConferenceEvent | null;
  onSaved: (saved: AdminEvent) => void;
  workspaceId: string | null;
}) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<EventForm>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      name: '', description: '', date_start: '', date_end: '',
      location: '', venue: '', timezone: 'America/New_York',
      status: 'draft', public_preview: false, website: '', image_url: '',
    },
  });
  const [pendingSave, setPendingSave] = useState<EventForm | null>(null);

  useEffect(() => {
    if (open) {
      reset(event ? {
        name: event.name,
        description: event.description ?? '',
        date_start: event.date_start,
        date_end: event.date_end,
        location: event.location,
        venue: event.venue ?? '',
        timezone: event.timezone,
        status: event.status,
        max_attendees: event.max_attendees ?? undefined,
        website: event.website ?? '',
        image_url: event.image_url ?? '',
        public_preview: event.public_preview ?? false,
      } : {
        name: '', description: '', date_start: '', date_end: '',
        location: '', venue: '', timezone: 'America/New_York',
        status: 'draft', public_preview: false, website: '', image_url: '',
      });
    }
  }, [open, event, reset]);

  const persist = async (data: EventForm) => {
    const payload = {
      ...data,
      status: event ? data.status : 'draft',
      website:      data.website      || null,
      image_url:    data.image_url    || null,
      description:  data.description  || null,
      venue:        data.venue        || null,
      max_attendees: data.max_attendees || null,
      public_preview: data.public_preview ?? false,
      ...(!event ? {
        home_layout: createRecommendedHomeLayout(),
        app_modules: manualEventModuleDefaults(),
      } : {}),
    };

    try {
      let saved: AdminEvent;
      if (event) saved = await adminEventService.update(event.id, payload);
      else if (workspaceId) saved = await adminEventService.create(workspaceId, payload);
      else throw new Error('Choose an approved workspace before creating an event');
      toast.success(event ? 'Event updated' : 'Event created');
      setPendingSave(null); onSaved(saved);
      onClose();
    } catch (err) {
      toast.error('Save failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };
  const prepareSave = (data: EventForm) => event ? setPendingSave(data) : persist(data);

  return (
    <Modal open={open} onClose={onClose} title={event ? 'Edit Event' : 'Create Event'} size="xl">
      <form onSubmit={handleSubmit(prepareSave)} className="space-y-4">
        <Input label="Event Name *" placeholder="AI & ML Conference 2026" error={errors.name?.message} {...register('name')} />
        <Textarea label="Description" placeholder="A short description visible to attendees…" rows={2} {...register('description')} />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Start Date *" type="date" error={errors.date_start?.message} {...register('date_start')} />
          <Input label="End Date *"   type="date" error={errors.date_end?.message}   {...register('date_end')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Location *" placeholder="San Francisco, CA" error={errors.location?.message} {...register('location')} />
          <Input label="Venue"      placeholder="Moscone Center"  {...register('venue')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Timezone *"
            options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            {...register('timezone')}
          />
          {event && <SelectField
              label="Status *"
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'published', label: 'Published' },
                { value: 'live', label: 'Live' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              {...register('status')}
            />}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Max Attendees" type="number" placeholder="2400" {...register('max_attendees')} />
          <Input label="Website URL" placeholder="https://…" {...register('website')} />
        </div>

        <Input label="Cover Image URL" placeholder="https://images.unsplash.com/…" {...register('image_url')} />

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" className="w-4 h-4 rounded accent-sky-500" {...register('public_preview')} />
          <span className="text-sm text-gray-300">
            Allow public preview
            <span className="block text-xs text-gray-500">Non-members can browse sessions, speakers, and exhibitors (no AI access).</span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>{event ? 'Save Changes' : 'Create Event'}</Button>
        </div>
      </form>
      <ConfirmDialog open={pendingSave !== null} onClose={() => setPendingSave(null)} onConfirm={() => pendingSave && persist(pendingSave)} intent="primary" title="Save changes?" description="Apply the current event changes?" confirmLabel="Save changes" />
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EventsPage() {
  const navigate = useNavigate();
  const { activeWorkspaceId, activeWorkspace, canCreateEvents } = useAdminAuth();
  const [events, setEvents]     = useState<AdminEvent[]>([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModal]   = useState(false);
  const [editing, setEditing]   = useState<AdminEvent | null>(null);
  const [deleteTarget, setDel]  = useState<AdminEvent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [aiEditingIds, setAiEditingIds] = useState<Set<string>>(new Set());
  const [aiEditError, setAiEditError] = useState<string | null>(null);
  const latestAiEditRequest = useRef(0);

  async function load() {
    setLoading(true);
    try {
      setEvents(activeWorkspaceId && activeWorkspace?.status === 'approved'
        ? await adminEventService.list(activeWorkspaceId)
        : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [activeWorkspaceId, activeWorkspace?.status]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminEventService.remove(deleteTarget.id);
      toast.success('Event deleted');
      setDel(null);
      load();
    } catch (err) {
      toast.error('Delete failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDeleting(false);
    }
  }

  function openEdit(event: AdminEvent) { setEditing(event); setModal(true); }
  function openCreate()                      { setEditing(null);  setModal(true); }

  async function editWithAI(eventId: string) {
    const request = ++latestAiEditRequest.current;
    setAiEditingIds((current) => new Set(current).add(eventId));
    setAiEditError(null);
    try {
      const draft = await eventBuilderService.editEvent(eventId);
      if (request === latestAiEditRequest.current) navigate(`/ai-event-builder/${draft.id}`);
    } catch (error) {
      if (request === latestAiEditRequest.current) {
        setAiEditError(error instanceof Error ? error.message : 'Unable to start AI editing');
      }
    } finally {
      setAiEditingIds((current) => {
        const next = new Set(current);
        next.delete(eventId);
        return next;
      });
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Events"
        description="Create and manage conference events."
        action={canCreateEvents ? <div className="flex items-center gap-2">
          <Link to="/ai-event-builder/new" className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-100 hover:bg-gray-700">
            <Sparkles size={14} /> Create with AI
          </Link>
          <Button icon={<Plus size={14} />} onClick={openCreate}>New Event</Button>
        </div> : undefined}
      />

      {aiEditError && <p role="alert" className="mb-4 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">{aiEditError}</p>}

      {loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No events yet"
          description={canCreateEvents ? 'Create your first conference event to get started.' : 'No events have been assigned to you in this workspace.'}
          action={canCreateEvents ? <Button icon={<Plus size={14} />} onClick={openCreate}>Create Event</Button> : undefined}
        />
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const canEdit = event.effective_role === 'admin' || event.effective_role === 'manager';
            const canDelete = event.effective_role === 'admin';
            return (
            <div
              key={event.id}
              className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors"
            >
              <div className="flex items-start gap-4 p-5">
                {/* Cover thumbnail */}
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                  {event.image_url ? (
                    <img src={event.image_url} alt={event.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Calendar size={20} className="text-gray-600" />
                    </div>
                  )}
                </div>

                {/* Workspace entry */}
                <Link to={`/events/${event.id}/overview`} className="flex-1 min-w-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-white truncate hover:text-sky-300 transition-colors">{event.name}</h3>
                    {statusBadge(event.status)}
                    <Badge variant="default">{event.effective_role}</Badge>
                    {event.public_preview && (
                      <span className="inline-flex items-center gap-1 text-xs text-sky-400 bg-sky-900/30 px-1.5 py-0.5 rounded">
                        <Eye size={10} /> Preview
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {new Date(`${event.date_start}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' – '}
                      {new Date(`${event.date_end}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1"><MapPin size={11} />{event.location}</span>
                    {event.max_attendees && (
                      <span className="flex items-center gap-1"><Users size={11} />{event.max_attendees.toLocaleString()} max</span>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{event.description}</p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-sky-400">
                    Open workspace <ArrowRight size={12} />
                  </span>
                </Link>

                <div className="flex self-stretch items-center gap-1 flex-shrink-0" role="group" aria-label={`Actions for ${event.name}`}>
                  <div className="flex min-w-24 flex-col justify-center gap-2">
                    {canEdit && <Button variant="secondary" size="sm" className="justify-center" icon={<Pencil size={13} />} onClick={() => openEdit(event)}>Edit</Button>}
                    {canEdit && <Button variant="secondary" size="sm" className="justify-center" icon={<Sparkles size={13} />} loading={aiEditingIds.has(event.id)} onClick={() => void editWithAI(event.id)}>Edit with AI</Button>}
                    {event.website && (
                      <a href={event.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-100 hover:bg-gray-700 transition-colors">
                        <ExternalLink size={13} /> Website
                      </a>
                    )}
                  </div>
                  {canDelete && <button type="button" aria-label={`Delete ${event.name}`} onClick={() => setDel(event)} className="self-center rounded p-2 text-gray-500 hover:bg-red-500/10 hover:text-red-300 transition-colors">
                    <Trash2 size={15} />
                  </button>}
                </div>
                </div>
              </div>

          );})}
        </div>
      )}

      <EventModal
        open={modalOpen}
        onClose={() => setModal(false)}
        event={editing}
        onSaved={(saved) => {
          void load();
          if (!editing) navigate(saved.effective_content_ingestion_enabled
            ? `/events/${saved.id}/content/setup`
            : `/events/${saved.id}/overview`);
        }}
        workspaceId={activeWorkspaceId}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDel(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete Event"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will permanently remove all associated sessions, papers, and data.`}
      />
    </div>
  );
}
